import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Trash2 } from 'lucide-react';
import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import {
  useClearAllNotifications,
  useDeleteNotification,
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useNotifications,
  useUnreadNotificationCount,
} from '../../hooks/queries/useNotifications';
import { useSidecarStore } from '../../store/useSidecarStore';
import { useTaskModalStore } from '../../store/taskModalStore';
import {
  getNotificationTarget,
  NOTIFICATION_TYPE_ICONS as TYPE_ICONS,
  NOTIFICATION_TYPE_LABELS as TYPE_LABELS,
} from '../../services/notifications/notificationTargets';
import type { Notification } from '../../services/notifications/notificationService';

function groupByDate(notifications: Notification[]) {
  const groups = new Map<string, Notification[]>();
  for (const n of notifications) {
    const date = n.created_at ? new Date(n.created_at) : new Date();
    let key: string;
    if (isToday(date)) key = "Aujourd'hui";
    else if (isYesterday(date)) key = 'Hier';
    else key = format(date, 'd MMMM yyyy', { locale: fr });
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(n);
  }
  return Array.from(groups.entries());
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const { data: notifications = [], isLoading } = useNotifications(100);
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();
  const deleteOne = useDeleteNotification();
  const clearAll = useClearAllNotifications();

  const selectSidecarChannel = useSidecarStore((s) => s.selectChannel);
  const openTask = useTaskModalStore((s) => s.openTask);

  function handleClick(n: Notification) {
    if (!n.is_read) markAsRead.mutate(n.id);
    const target = getNotificationTarget(n);
    if (!target) return;
    if (target.type === 'url') navigate(target.url);
    else if (target.type === 'task') openTask(target.taskId);
    else selectSidecarChannel(target.channelId, target.messageId);
  }

  const filtered = notifications.filter((n) => n.type !== 'dm' && n.type !== 'mention');
  const groups = groupByDate(filtered);

  return (
    <div className="main-container-gdm">
    <div className="mx-auto max-w-[860px] space-y-6">
      {unreadCount > 0 || filtered.length > 0 ? (
        <div className="flex items-center justify-end gap-2">
          {unreadCount > 0 && (
            <Button variant="secondary" size="sm" onClick={() => markAllAsRead.mutate()}>
              <CheckCheck className="w-3.5 h-3.5" />
              Tout marquer comme lu
            </Button>
          )}
          {filtered.length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (window.confirm('Supprimer toutes les notifications ?')) {
                  clearAll.mutate();
                }
              }}
            >
              <Trash2 className="w-3.5 h-3.5" />
              Tout effacer
            </Button>
          )}
        </div>
      ) : null}
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center bg-cime-surface rounded-cime border border-neutral-200">
          <Bell className="w-12 h-12 text-neutral-300 mb-4" aria-hidden />
          <p className="text-sm font-medium text-neutral-500">Aucune notification</p>
          <p className="text-xs text-neutral-400 mt-1">Vos notifications apparaîtront ici.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(([label, items]) => (
            <div key={label}>
              <h2 className="mb-2 [font-family:var(--cime-font-body,inherit)] text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-400">
                {label}
              </h2>
              <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
                {items.map((n) => (
                  <div
                    key={n.id}
                    className={`group flex gap-3 px-4 py-3 border-b border-neutral-50 last:border-b-0 hover:bg-neutral-50 transition-colors cursor-pointer ${
                      !n.is_read ? 'bg-cime-primary/[0.03]' : ''
                    }`}
                    onClick={() => handleClick(n)}
                  >
                    <div className="text-xl flex-shrink-0 leading-none pt-0.5">
                      {TYPE_ICONS[n.type] ?? '🔔'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <p className="text-sm font-medium text-neutral-800 flex-1 truncate">
                          {n.title}
                        </p>
                        <span className="text-[10px] text-neutral-400 flex-shrink-0">
                          {TYPE_LABELS[n.type] ?? n.type}
                        </span>
                        {!n.is_read && (
                          <span className="w-2 h-2 rounded-full bg-cime-primary flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-neutral-500 mt-1">{n.message}</p>
                    </div>
                    <div className="w-[150px] flex-shrink-0 text-right leading-tight">
                      {n.created_at ? (
                        <>
                          <span className="block text-[13px] text-cime-text-primary">
                            {format(new Date(n.created_at), 'd MMM yyyy · HH:mm', { locale: fr })}
                          </span>
                          <span className="block text-[11px] text-neutral-400">
                            {formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: fr })}
                          </span>
                        </>
                      ) : null}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteOne.mutate(n.id);
                      }}
                      title="Supprimer"
                      className="p-1.5 rounded text-neutral-400 hover:text-red-600 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity self-start"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </div>
  );
}
